/**
 * Platform-agnostic RevenueCat wrapper.
 *
 * The two underlying SDKs (`@revenuecat/purchases-capacitor` and
 * `@revenuecat/purchases-js`) have different APIs and ship different heavy code
 * paths, so we lazy-load whichever one the current runtime needs via dynamic
 * `import()` and expose a single surface to the rest of the app.
 *
 * Terms used in this module:
 *   - "native":  iOS / Android via Capacitor plugin (purchases-capacitor)
 *   - "web":     any non-Capacitor environment (purchases-js, Stripe-backed)
 *
 * The rest of the app only ever imports from this file; it never touches the
 * RevenueCat SDKs directly.
 */

import { Capacitor } from '@capacitor/core';

type Platform = 'ios' | 'android' | 'web';

const PLATFORM: Platform = ((): Platform => {
  const raw = Capacitor.getPlatform();
  return raw === 'ios' || raw === 'android' ? raw : 'web';
})();

const IOS_KEY = process.env.REACT_APP_RC_IOS_KEY ?? '';
const ANDROID_KEY = process.env.REACT_APP_RC_ANDROID_KEY ?? '';
const WEB_KEY = process.env.REACT_APP_RC_WEB_KEY ?? '';
const ENTITLEMENT_ID = process.env.REACT_APP_RC_ENTITLEMENT ?? 'pro';
const RC_DEBUG_LOGS =
  process.env.NODE_ENV === 'development' ||
  process.env.REACT_APP_RC_DEBUG === 'true';

/**
 * Normalized shape we expose for packages. `_native` carries the original
 * RevenueCat object because native vs web purchase APIs differ and we need to
 * pass the correct type back into the SDK when the user taps Subscribe.
 */
export interface NormalizedPackage {
  identifier: string;
  packageType: string;
  priceFormatted: string;
  priceMicros: number;
  currency: string;
  periodIso: string | null;
  /** Non-serializable reference back to the SDK's native package object. */
  _native: unknown;
}

/**
 * Subset of customer info the app needs. We intentionally do not re-export the
 * SDK's full type to keep the contract narrow and platform-agnostic.
 */
export interface NormalizedCustomerInfo {
  isPro: boolean;
  managementUrl: string | null;
  originalAppUserId: string;
  raw: unknown;
}

export interface NormalizedOfferings {
  current: {
    identifier: string;
    monthly: NormalizedPackage | null;
    annual: NormalizedPackage | null;
    all: NormalizedPackage[];
  } | null;
}

type CustomerInfoListener = (info: NormalizedCustomerInfo) => void;

interface NativeSdk {
  kind: 'native';
  mod: typeof import('@revenuecat/purchases-capacitor');
}
interface WebSdk {
  kind: 'web';
  mod: typeof import('@revenuecat/purchases-js');
  instance: import('@revenuecat/purchases-js').Purchases;
}

let sdkPromise: Promise<NativeSdk | WebSdk> | null = null;
let currentAppUserId: string | null = null;
let postPurchaseRefresh: (() => Promise<void>) | null = null;
let serverProPoll: (() => Promise<boolean>) | null = null;
/**
 * Shared abort signal for the active `waitForServerPro` backoff chain. Flipped
 * to a new controller on each invocation so a newer purchase cancels an older
 * in-flight poll (edge case: user subscribes twice quickly, or signs out
 * mid-poll). Also aborted from `logOut()` so a stale poll can't clobber state
 * after the user left.
 */
let activePollController: AbortController | null = null;
const listeners = new Set<CustomerInfoListener>();

/**
 * Set a callback that the purchase flow invokes right after a successful
 * purchase / restore, before resolving. The App layer plugs in Clerk's
 * `user.reload() + getToken({ skipCache: true })` so the next API request
 * carries a JWT with the freshly-minted `pro` claim.
 */
export function configurePostPurchaseRefresh(
  fn: (() => Promise<void>) | null
): void {
  postPurchaseRefresh = fn;
}

/**
 * Set a callback the purchase flow polls after refreshing the Clerk token.
 * The callback should return `true` when the server confirms the current
 * user is Pro (i.e. the RC -> Clerk webhook has landed and the next JWT
 * carries `pro: true`).
 *
 * Without this poll, the user's next scan call after paying can still 402
 * `scan_cap` because the RC webhook -> Clerk Backend API -> Clerk session
 * token hydration path is async (typically 1-5 s).
 */
export function configureServerProPoll(
  fn: (() => Promise<boolean>) | null
): void {
  serverProPoll = fn;
}

function keyForPlatform(): string {
  switch (PLATFORM) {
    case 'ios':
      return IOS_KEY;
    case 'android':
      return ANDROID_KEY;
    default:
      return WEB_KEY;
  }
}

function isTestStoreKey(apiKey: string): boolean {
  return apiKey.startsWith('test_');
}

function logRevenueCatDebug(message: string): void {
  if (!RC_DEBUG_LOGS) return;
  // Keep the key redacted but include enough shape to spot env mix-ups.
  console.warn(`[RevenueCat] ${message}`);
}

async function installNativeDebugLogging(
  mod: typeof import('@revenuecat/purchases-capacitor')
): Promise<void> {
  if (!RC_DEBUG_LOGS) return;
  try {
    await mod.Purchases.setLogLevel({ level: mod.LOG_LEVEL.DEBUG });
    await mod.Purchases.setLogHandler((level, message) => {
      console.warn(`[RevenueCat:${level}] ${message}`);
    });
  } catch {
    /* Debug logging must never block purchase setup. */
  }
}

async function loadSdk(appUserId: string): Promise<NativeSdk | WebSdk> {
  const apiKey = keyForPlatform();
  if (!apiKey) {
    throw new Error(
      `RevenueCat ${PLATFORM} API key missing. Set REACT_APP_RC_${PLATFORM.toUpperCase()}_KEY.`
    );
  }
  // RevenueCat Test Store is storeless. If a local/native build is configured
  // with a `test_` key, use the JS SDK inside the WebView so StoreKit/Play
  // product availability cannot block quick iteration.
  if ((PLATFORM === 'ios' || PLATFORM === 'android') && !isTestStoreKey(apiKey)) {
    const mod = await import('@revenuecat/purchases-capacitor');
    await installNativeDebugLogging(mod);
    logRevenueCatDebug(
      `configure platform=${PLATFORM} sdk=native keyPrefix=${apiKey.slice(0, 5)} keyLength=${apiKey.length}`
    );
    await mod.Purchases.configure({ apiKey, appUserID: appUserId });
    mod.Purchases.addCustomerInfoUpdateListener((info) => {
      emit(normalizeCustomerInfo(info));
    });
    return { kind: 'native', mod };
  }
  const mod = await import('@revenuecat/purchases-js');
  logRevenueCatDebug(
    `configure platform=${PLATFORM} sdk=web keyPrefix=${apiKey.slice(0, 5)} keyLength=${apiKey.length}`
  );
  const instance = mod.Purchases.configure({ apiKey, appUserId });
  return { kind: 'web', mod, instance };
}

function emit(info: NormalizedCustomerInfo): void {
  listeners.forEach((l) => {
    try {
      l(info);
    } catch {
      /* listener errors must not poison the RC callback */
    }
  });
}

export function onCustomerInfoUpdate(fn: CustomerInfoListener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getPlatform(): Platform {
  return PLATFORM;
}

export function getEntitlementId(): string {
  return ENTITLEMENT_ID;
}

/**
 * Configure (on first call) and identify the SDK as `appUserId`. Safe to call
 * repeatedly for the same user; becomes a no-op past the first successful
 * identification.
 */
export async function login(appUserId: string): Promise<NormalizedCustomerInfo> {
  if (!appUserId) throw new Error('purchasesService.login requires a non-empty appUserId');

  if (!sdkPromise) {
    sdkPromise = loadSdk(appUserId);
    currentAppUserId = appUserId;
    const sdk = await sdkPromise;
    return fetchCustomerInfoFrom(sdk);
  }

  const sdk = await sdkPromise;
  if (currentAppUserId === appUserId) {
    return fetchCustomerInfoFrom(sdk);
  }
  currentAppUserId = appUserId;
  if (sdk.kind === 'native') {
    const res = await sdk.mod.Purchases.logIn({ appUserID: appUserId });
    return normalizeCustomerInfo(res.customerInfo);
  }
  const info = await sdk.instance.changeUser(appUserId);
  return normalizeCustomerInfo(info);
}

/**
 * Log out. On native this clears the app user id and returns to an anonymous
 * RC user. On web there is no true `logOut`; we rotate to a fresh anonymous id
 * so the app user can't accidentally inherit a previous user's entitlements.
 */
export async function logOut(): Promise<void> {
  if (!sdkPromise) return;
  // Cancel any in-flight server-pro polling — the user we were waiting on
  // is gone, and the next log-in installs a fresh controller anyway.
  activePollController?.abort();
  activePollController = null;
  const sdk = await sdkPromise;
  if (sdk.kind === 'native') {
    await sdk.mod.Purchases.logOut();
  } else {
    const anonId = sdk.mod.Purchases.generateRevenueCatAnonymousAppUserId();
    await sdk.instance.changeUser(anonId);
  }
  currentAppUserId = null;
}

export async function getCustomerInfo(): Promise<NormalizedCustomerInfo> {
  const sdk = await ensureReady();
  return fetchCustomerInfoFrom(sdk);
}

async function fetchCustomerInfoFrom(
  sdk: NativeSdk | WebSdk
): Promise<NormalizedCustomerInfo> {
  if (sdk.kind === 'native') {
    const { customerInfo } = await sdk.mod.Purchases.getCustomerInfo();
    return normalizeCustomerInfo(customerInfo);
  }
  const info = await sdk.instance.getCustomerInfo();
  return normalizeCustomerInfo(info);
}

export async function getOfferings(): Promise<NormalizedOfferings> {
  const sdk = await ensureReady();
  if (sdk.kind === 'native') {
    const offerings = await sdk.mod.Purchases.getOfferings();
    const cur = offerings.current;
    if (!cur) return { current: null };
    const monthly = cur.monthly ? normalizeNativePackage(cur.monthly) : null;
    const annual = cur.annual ? normalizeNativePackage(cur.annual) : null;
    const all = cur.availablePackages.map(normalizeNativePackage);
    logRevenueCatDebug(
      `offerings platform=${PLATFORM} current=${cur.identifier} packages=${all
        .map((pkg) => pkg.identifier)
        .join(',') || 'none'}`
    );
    return { current: { identifier: cur.identifier, monthly, annual, all } };
  }
  const offerings = await sdk.instance.getOfferings();
  const cur = offerings.current;
  if (!cur) return { current: null };
  const monthly = cur.monthly ? normalizeWebPackage(cur.monthly) : null;
  const annual = cur.annual ? normalizeWebPackage(cur.annual) : null;
  const all = cur.availablePackages.map(normalizeWebPackage);
  logRevenueCatDebug(
    `offerings platform=web current=${cur.identifier} packages=${all
      .map((pkg) => pkg.identifier)
      .join(',') || 'none'}`
  );
  return { current: { identifier: cur.identifier, monthly, annual, all } };
}

function normalizeNativePackage(
  p: import('@revenuecat/purchases-capacitor').PurchasesPackage
): NormalizedPackage {
  const product = p.product;
  return {
    identifier: p.identifier,
    packageType: p.packageType,
    priceFormatted: product.priceString,
    priceMicros: Math.round(product.price * 1_000_000),
    currency: product.currencyCode,
    periodIso: product.subscriptionPeriod ?? null,
    _native: p,
  };
}

function normalizeWebPackage(
  p: import('@revenuecat/purchases-js').Package
): NormalizedPackage {
  const product = p.webBillingProduct;
  return {
    identifier: p.identifier,
    packageType: p.packageType,
    priceFormatted: product.price.formattedPrice,
    priceMicros: product.price.amountMicros,
    currency: product.price.currency,
    periodIso: product.normalPeriodDuration ?? null,
    _native: p,
  };
}

/**
 * Execute a purchase. On success, invokes the post-purchase refresh callback
 * (Clerk session token refresh) before resolving so the very next API call
 * carries the updated `pro` claim.
 */
function startFreshPollController(): AbortSignal {
  activePollController?.abort();
  const controller = new AbortController();
  activePollController = controller;
  return controller.signal;
}

export async function purchase(pkg: NormalizedPackage): Promise<NormalizedCustomerInfo> {
  const sdk = await ensureReady();
  let info: NormalizedCustomerInfo;
  if (sdk.kind === 'native') {
    const native = pkg._native as import('@revenuecat/purchases-capacitor').PurchasesPackage;
    const res = await sdk.mod.Purchases.purchasePackage({ aPackage: native });
    info = normalizeCustomerInfo(res.customerInfo);
  } else {
    const webPkg = pkg._native as import('@revenuecat/purchases-js').Package;
    const res = await sdk.instance.purchase({ rcPackage: webPkg });
    info = normalizeCustomerInfo(res.customerInfo);
  }
  await runPostPurchaseRefresh();
  if (info.isPro) {
    // Block until the server agrees - protects the very next API call from
    // 402-ing on a stale JWT. Cancellable so a logOut or follow-up purchase
    // doesn't leak the backoff chain.
    await waitForServerPro(startFreshPollController());
  }
  emit(info);
  return info;
}

export async function restore(): Promise<NormalizedCustomerInfo> {
  const sdk = await ensureReady();
  let info: NormalizedCustomerInfo;
  if (sdk.kind === 'native') {
    const res = await sdk.mod.Purchases.restorePurchases();
    info = normalizeCustomerInfo(res.customerInfo);
  } else {
    // purchases-js identifies users by id, so "restore" collapses to
    // re-reading customer info for the current app user id.
    const ci = await sdk.instance.getCustomerInfo();
    info = normalizeCustomerInfo(ci);
  }
  await runPostPurchaseRefresh();
  if (info.isPro) await waitForServerPro(startFreshPollController());
  emit(info);
  return info;
}

async function runPostPurchaseRefresh(): Promise<void> {
  const fn = postPurchaseRefresh;
  if (!fn) return;
  try {
    await fn();
  } catch {
    /* refresh failure shouldn't break a successful purchase — next
       API call will retry on its own token cache miss. */
  }
}

/**
 * After the JWT is refreshed, wait for the SERVER (via /v1/me/subscription)
 * to agree the user is Pro. Caps at ~10s with exponential-ish backoff
 * (0.3, 0.6, 1.0, 1.5, 2.0, 2.5, 2.5 s). Returns early on first `true`,
 * on abort, or after the budget so a flaky network doesn't block the
 * purchase dialog from closing.
 */
async function waitForServerPro(signal?: AbortSignal): Promise<void> {
  const poll = serverProPoll;
  if (!poll) return;
  if (signal?.aborted) return;
  const delaysMs = [300, 600, 1000, 1500, 2000, 2500, 2500];
  for (const delay of delaysMs) {
    if (signal?.aborted) return;
    try {
      if (await poll()) return;
    } catch {
      /* transient error - try again after the delay */
    }
    if (signal?.aborted) return;
    await new Promise<void>((resolve) => {
      const timer = setTimeout(resolve, delay);
      if (signal) {
        const onAbort = (): void => {
          clearTimeout(timer);
          signal.removeEventListener('abort', onAbort);
          resolve();
        };
        signal.addEventListener('abort', onAbort, { once: true });
      }
    });
  }
  if (signal?.aborted) return;
  try {
    // Final attempt after the last delay, so the total budget isn't wasted.
    await poll();
  } catch {
    /* give up quietly */
  }
}

async function ensureReady(): Promise<NativeSdk | WebSdk> {
  if (!sdkPromise) {
    throw new Error(
      'purchasesService used before login(). Call login(userId) after Clerk auth resolves.'
    );
  }
  return sdkPromise;
}

type AnyCustomerInfo =
  | import('@revenuecat/purchases-js').CustomerInfo
  | import('@revenuecat/purchases-capacitor').CustomerInfo;

function normalizeCustomerInfo(info: AnyCustomerInfo): NormalizedCustomerInfo {
  const ent = info.entitlements.active[ENTITLEMENT_ID];
  return {
    isPro: !!ent && ent.isActive !== false,
    managementUrl: info.managementURL ?? null,
    originalAppUserId: info.originalAppUserId,
    raw: info,
  };
}
